// =====================================================
// Dropdown Select Component
// =====================================================

import React, { useState } from 'react';
import { DropdownOption } from '../types';
import { useChatStore } from '../store/chatStore';
import { sendMessage } from '../services/api';

interface DropdownProps {
  options: DropdownOption[];
  placeholder?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  placeholder = 'Select an option...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>('');
  const { sessionId, userId, language, currentPage, addMessage, setIsLoading } = useChatStore();

  const handleSelect = async (option: DropdownOption) => {
    setSelectedValue(option.value);
    setIsOpen(false);

    // Add user message showing what they selected
    addMessage({
      id: `msg_${Date.now()}`,
      sender: 'user',
      text: option.label,
      timestamp: new Date()
    });

    setIsLoading(true);

    try {
      // Send the action to the backend
      const response = await sendMessage({
        session_id: sessionId,
        user_id: userId,
        message: option.value,
        language,
        current_url: currentPage
      });

      // Add bot response
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: response.message,
        timestamp: new Date(),
        buttons: response.buttons,
        card: response.card,
        cards: response.cards,
        dropdown: response.dropdown,
        showAppointmentForm: response.showAppointmentForm
      });
    } catch (error) {
      console.error('Error sending dropdown selection:', error);
      addMessage({
        id: `msg_${Date.now() + 1}`,
        sender: 'bot',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedOption = options.find(opt => opt.value === selectedValue);

  return (
    <div className="relative w-full">
      {/* Dropdown trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 bg-white border border-betenbough-sage rounded-lg
                   flex items-center justify-between text-left text-sm
                   hover:border-betenbough-olive hover:bg-betenbough-sage/10 transition-colors
                   focus:outline-none focus:ring-2 focus:ring-betenbough-citron"
      >
        <span className={selectedOption ? 'text-betenbough-gunmetal' : 'text-gray-500'}>
          {selectedOption ? (
            <span className="text-sm">
              {selectedOption.label}
              {selectedOption.sublabel && (
                <span className="text-gray-400 text-xs ml-1">({selectedOption.sublabel})</span>
              )}
            </span>
          ) : (
            <span className="text-sm">{placeholder}</span>
          )}
        </span>
        <svg
          className={`w-4 h-4 text-betenbough-olive transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown options list */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-betenbough-sage
                        rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((option, index) => (
            <button
              key={`${option.value}-${index}`}
              onClick={() => handleSelect(option)}
              className="w-full px-3 py-2 text-left text-sm
                         hover:bg-betenbough-citron/30 hover:text-betenbough-gunmetal
                         transition-colors border-b border-betenbough-sage/20 last:border-b-0
                         flex flex-col"
            >
              <span className="text-betenbough-gunmetal font-medium text-sm">
                {option.label}
              </span>
              {option.sublabel && (
                <span className="text-betenbough-myrtle text-xs">
                  {option.sublabel}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
